import { CommonModule, KeyValuePipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { WmsDialogComponent, WmsDialogHeaderComponent, WmsDialogBodyComponent, WmsDialogFooterComponent, BaseComponent, WmsSplitPaneComponent, WmsSplitPanelComponent, WmsMessageComponent } from '@wms/components';
import { forEach } from "lodash-es";
import { getVariableCategoryList, getDuplicateVariables, getVariableServiceType } from '@wms/variables';
import { FormsModule } from '@angular/forms';
import { SearchComponent } from '@wms/components';
import { BaseVariableService } from '@wms/variables';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from "ngx-bootstrap/tabs";
import { DialogService, PageService, ProjectService } from '@wms/services';
import { BaseVariablePropertyFactoryService, AddVariableConfigComponent } from '@wms/variables';
import { VariablesTemplateComponent, VARIABLE_CONSTANTS, VariableCrudManagerService, VariableTabViewComponent } from '@wms/variables';
import { FilterPipe } from '@wm/components-base';
import { formatVariableIconClass } from '@wms/app/core';
import { Subscription } from 'rxjs';
import { PageWsService } from '@wms/workspace';
import {CommonWidgetDirective} from "@wms/directives";

@Component({
  standalone: true,
  selector: 'wms-variable-config',
  templateUrl: './variable-config.component.html',
  styleUrls: ['./variable-config.component.less'],
  imports: [
    CommonModule,
    FormsModule,
    TooltipModule,
    TabsModule,
    CommonWidgetDirective,
    WmsDialogComponent,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsSplitPaneComponent,
    WmsSplitPanelComponent,
    SearchComponent,
    WmsMessageComponent,
    AddVariableConfigComponent,
    VariablesTemplateComponent,
    FilterPipe,
    KeyValuePipe,
    VariableTabViewComponent
  ]
})
export class VariableConfigComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() variableView: string;
  @Output() onModalClose: EventEmitter<any> = new EventEmitter<any>();
  variableCollectionType: any;
  variableCategoryList: any;
  categoryFilterList: any;
  ownerApp = VARIABLE_CONSTANTS.OWNER.APP;
  ownerPage = VARIABLE_CONSTANTS.OWNER.PAGE;
  duplicateVariables: any;
  filteredVariableList: any;
  noVariableFound: any;
  variableCategoryFilter: string = 'all';
  variableFilter: any = {
    category: '',
    name: '',
    _serviceType: ''
  };
  disableSave: any;
  hideVariables: any = {};
  variableOwnersList: string[];
  variableResults: any = {};
  selectedVariable: any;
  variableService: any;
  reloadVarSubscription: Subscription;
  changeOwnerSubscription: Subscription;
  unfilteredVarResults: any;
  actionVariables = ['wm.LoginVariable', 'wm.LogoutVariable', 'wm.NavigationVariable', 'wm.NotificationVariable', 'wm.TimerVariable'];

  constructor(private dialogService: DialogService, public VariableCrudManagerService: VariableCrudManagerService, private pageService: PageService,public pageWsService: PageWsService) {
    super();
    this.variableService = inject(BaseVariableService);
    /*Vary the owners list based on the project type*/
    if (this.projectService.isPrefabType()) {
      this.variableOwnersList = [this.ownerPage];
    } else {
      this.variableOwnersList = [this.ownerPage, this.ownerApp];
    }
    this.variableOwnersList.forEach((owner) => {
      this.hideVariables[owner] = false;
      this.variableResults[owner] = [];
    });
    this.reloadVarSubscription = this.variableService.reloadVariablesObs().subscribe((isVarDialog: boolean) => {
      if (isVarDialog) {
        this.closeModal();
      } else {
        this.closeDialogWithId('addVariableModal');
        this.initDialog();
      }
      this.eventManager.emitUpdateVariableTabView();
    });
    this.changeOwnerSubscription = this.VariableCrudManagerService.changeOwnerObs().subscribe(() => {
      this.initVariableListByType();
    });
    this.VariableCrudManagerService.addVariableDialogMsg = null;
    this.variableService.variableDialogMsg = null;
  }

  ngOnInit(): void {
    this.variableService.currentVariableView = this.variableView;
    this.initDialog();
  }

  closeModal() {
    this.variableService.selectedVariable = undefined;
    this.variableService.currentVariableView = '';
    this.onModalClose.emit();
  }

  hasText(s1: string, s2: string): boolean {
    return s1.toLowerCase().includes(s2.toLowerCase());
  }

  filterVariableList(event: any, searchNode?: any) {
    if (event === 'all' && searchNode) {
      this.variableResults = this.unfilteredVarResults;
      return;
    }
    else if(!searchNode) {
      // if we have search text then node will be expanded(caret-down) and results are shown.
      this.variableFilter.name = event;
    }
    const searchTerm = event;
    let localFilteredList: any = { 'App': [], 'Page': [] };
    forEach(this.unfilteredVarResults, (item, key) => {
      item.forEach((node: any) => {
        if (this.hasText(node[searchNode] || node.name, searchTerm)) {
          localFilteredList[node.owner].push(node);
        }
      })
    });
    this.variableResults = localFilteredList;
  }

  closeDialogWithId(id: string): void {
    this.dialogService.close(id);
  }

  openDialogWithId(id: string): void {
    this.variableService.prevSelectedVariable = this.variableService.selectedVariable;
    // TO-DO - this should be refactored
    // selected variable value shouldn't persist once you are trying to create new variable/action
    this.variableService.selectedVariable = undefined;
    this.dialogService.open(id);
  }

  /* watching over the variable category filter, and assigning the value to the variableFilter object */
  setVariableFilter() {
    const newVal: String = this.variableCategoryFilter !== 'all' ? this.variableCategoryFilter : '';
    this.variableFilter._serviceType = newVal;
    this.filterVariableList(this.variableCategoryFilter, '_serviceType');
  };

  formatIconClass(variableCategory: any) {
    return formatVariableIconClass(variableCategory);
  }

  /* function to initialize variable category list based on current collection type */
  initVariableCategoryList() {
    /* get the supported variable categories */
    this.variableCategoryList = getVariableCategoryList(this.variableCollectionType);

    /*create a separate list containing option for showing all category variables */
    this.categoryFilterList = { 'all': this.locale.LABEL_INSERT_VARIABLE_FILTER_ALL };
    forEach(this.variableService.serviceTypes, (type) => {
      this.categoryFilterList[type] = this.locale.LABEL_VARIABLE_SERVICETYPE[type];
    });
  }

  /*Method to filter variableList based on the variableCategoryList*/
  initVariableListByType() {
    let requiredCollection: any = {}, tempCollection: any = [],
      variableCategoryList = this.variableCategoryList;
    this.duplicateVariables = getDuplicateVariables();
    /*Collect page level and global variables of the active page*/
    requiredCollection[this.ownerApp] = this.VariableCrudManagerService.variableList[this.ownerApp];
    requiredCollection[this.pageService.activePageName] = this.VariableCrudManagerService.variableList[this.pageService.activePageName];

    /* prepare the list in required format */
    forEach(requiredCollection, (variables: any, owner: any) => {
      forEach(variables, (variable, name) => {
        variable.name = name;
        variable.owner = owner !== this.ownerApp ? this.ownerPage : this.ownerApp;
        variable._serviceType = getVariableServiceType(variable);
        /* filter the list based on the variableCategoryList */
        if (variableCategoryList[variable.category]) {
          tempCollection.push(variable);
        }
      });
    });

    this.filteredVariableList = tempCollection;
    forEach(Object.keys(this.variableResults), (owner: any) => {
      const filteredList = this.filteredVariableList.filter((variableObj: any) => variableObj.owner === owner);
      const orderedList = filteredList.sort((a: any, b: any) => {
        // Sort by category first using localeCompare
        const categoryComparison = a.category.localeCompare(b.category);
        if (categoryComparison !== 0) {
          return categoryComparison; // If categories are different, return the comparison result
        }
        // If categories are the same, sort by name using localeCompare
        return a.name.localeCompare(b.name);
      });
      this.variableResults[owner] = orderedList;
    });
    this.unfilteredVarResults = this.variableResults;
    this.noVariableFound = tempCollection.length === 0;
  }

  /**
   * Selects the first page variable in the list
   * If page variable is not there then an application variable is selected
   */
  selectFirstVariable() {
    let pageVariableExists;
    if (this.filteredVariableList.length) {
      forEach(this.filteredVariableList, (variable): any => {
        if (variable.owner === this.variableOwnersList[0]) {
          this.VariableCrudManagerService.getTypesOfSelectedVariables(variable.category, variable);
          pageVariableExists = true;
          return false;
        }
      });
      if (!pageVariableExists)
        this.VariableCrudManagerService.getTypesOfSelectedVariables(this.filteredVariableList[0].category, this.filteredVariableList[0]);
    }
  };

  getValfromLocale(val: any) {
    return this.locale[val];
  }

  checkVariableCount(filteredVariableList: any) {
    this.disableSave = !filteredVariableList.length;
  }

  checkPrevAndCurrentVarType(): any {
    if (this.variableService.currentVariableView === 'Variables' && this.actionVariables.includes(this.variableService.selectedVariable.category || this.variableService.selectedVariable.variableObject.category)) {
      return true;
    } else if (this.variableService.currentVariableView === 'Actions' && !this.actionVariables.includes(this.variableService.selectedVariable.category || this.variableService.selectedVariable.variableObject.category)) {
      return true;
    }
  }

  /* function to initialize dialog properties and dependencies */
  initDialog() {
    this.variableCollectionType =  this.variableService.currentVariableView === 'Actions' ? 'action' : 'data';

    /* get the dialog header key */
    this.variableService.initHeaderOptions(this.variableView);

    // (9.0) initialize bucket list
    this.variableService.initServiceTypesList(this.variableCollectionType);

    // /* get the category list */
    this.initVariableCategoryList();

    // /* get the existing variables list */
    this.VariableCrudManagerService.initVariableList();

    // /*Filtering the variable list based on the variableCategoryList*/
    this.initVariableListByType();

    // /* initialize the property list for UI to consume */
    this.VariableCrudManagerService.initVariablePropertyList();

    // /* if pre-selected variable provided, select the variable else select first variable */
    if (this.variableService.selectedVariable &&  !this.checkPrevAndCurrentVarType() && this.checkForVarExistence()) {
      this.initPreselectedVariable(this.variableService.selectedVariable.variableObject);
      this.variableService.prevSelectedVariable = undefined;
      this.variableService.selectedEvt = undefined;
    } else {
      this.selectFirstVariable();
    }
    // }
    // /*If a message is passed with open dialog, show the message in variable dialog*/
    // if (message) {
    //     toggleMessage(true, message.text, message.type || 'info');
    // }
    this.checkVariableCount(this.filteredVariableList);
  }

  checkForVarExistence() {
    const owner = this.variableService.selectedVariable.variableObject.owner;
    return owner === 'Page' ? this.VariableCrudManagerService.variableList[this.pageService.activePageName][this.variableService.selectedVariable.variableObject.name] :
      this.VariableCrudManagerService.variableList['App'][this.variableService.selectedVariable.variableObject.name];
  }

  /* initialize the provided variable as selected in the dialog */
  initPreselectedVariable(variable: any) {
    let variableToSelect: any;

    if (this.variableService.prevSelectedVariable && this.variableService.selectedEvt) {
      this.filteredVariableList.forEach((variableObj: any) => {
        /* if variable found, break the loop */
        if (variableObj.name === variable.name) {
          Object.assign(variableObj, this.variableService.prevSelectedVariable.variableObject);
        }
      });
      this.VariableCrudManagerService.currentSelectedTabView = 'events';
    }

    /* based on the variable info provided, get the variable object to select from the list */
    this.filteredVariableList.every((variableObj: any) => {
      /* if variable found, break the loop */
      if (variableObj.name === variable.name) {
        variableToSelect = variableObj;
        return false;
      }
      return true;
    });

    /* initialize the new variable properties container object */
    this.variableService.selectedVariable = {};

    this.VariableCrudManagerService.getTypesOfSelectedVariables(variableToSelect.category, variableToSelect, false, undefined, false);
  }


  /**
   * Delete a variable and stop propogation to parent
   *
   */
  toggleVariableDelete($event: any, variableObject: any) {
    variableObject.remove = !variableObject.remove;
    $event.stopPropagation();
  }

  override ngOnDestroy(): void {
    if (this.reloadVarSubscription) {
      this.reloadVarSubscription.unsubscribe();
    }
    if (this.changeOwnerSubscription) {
      this.changeOwnerSubscription.unsubscribe();
    }
    this.variableService.prevSelectedVariable = undefined;
    this.variableService.selectedVariableView = '';
    this.variableService.disableDialogActions = false;
  }

}
